import { ChangeDetectionStrategy, Component, inject, Injector, Signal } from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { Store } from '@ngrx/store';
import {
    CartActions,
    CartFillable,
    selectCartReceiver,
    selectCartShop,
    selectCartTable,
    selectOrderType
} from '@us/web/cart/data-access';
import { tap } from 'rxjs';
import { TuiElasticStickyModule, TuiSheetOptions, TuiSheetService } from '@taiga-ui/addon-mobile';
import { IonicModule } from '@ionic/angular';
import {
    DeliveryAddress,
    FormattedAddressPipe,
    OrderShop,
    OrderTableNamePipe,
    OrderType,
    Table
} from '@us/web/shared/data-access';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { OrderTypeSelectorComponent } from '@us/web/orders/ui';
import { TuiSvgModule } from '@taiga-ui/core';
import { TranslocoModule } from '@ngneat/transloco';
import { selectOrderTypes } from '@us/web/shop/data-access';

@Component({
    selector: 'us-cart-order-type',
    standalone: true,
    imports: [
        NgIf,
        AsyncPipe,
        IonicModule,
        TuiSvgModule,
        FormattedAddressPipe,
        TranslocoModule,
        OrderTableNamePipe,
        TuiElasticStickyModule
    ],
    templateUrl: './cart-order-type.component.html',
    styleUrls: ['./cart-order-type.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CartOrderTypeComponent {
    private readonly store = inject(Store);
    private readonly injector = inject(Injector);
    private readonly sheetService = inject(TuiSheetService);

    readonly shop: Signal<OrderShop | undefined> = this.store.selectSignal(selectCartShop);
    readonly orderType: Signal<OrderType | null> = this.store.selectSignal(selectOrderType);
    readonly receiver: Signal<DeliveryAddress | null> = this.store.selectSignal(selectCartReceiver);
    readonly table: Signal<Table | undefined> = this.store.selectSignal(selectCartTable);
    readonly orderTypes: Signal<OrderType[]> = this.store.selectSignal(selectOrderTypes);

    readonly OrderType = OrderType;

    handleClickOrderType() {
        const orderTypes = this.orderTypes();
        if (!orderTypes.length) {
            return;
        }

        if (orderTypes.length === 1 && !this.orderType() && orderTypes[0] !== OrderType.OrderAtTheTable) {
            this.store.dispatch(CartActions.updateCart({ order_type: orderTypes[0] }));
            return;
        }

        this.sheetService
            .open<{
                orderType: OrderType;
                table?: Table;
                isChanged: boolean;
            }>(
                new PolymorpheusComponent(OrderTypeSelectorComponent, this.injector) as any,
                {
                    closeable: true,
                    overlay: true
                } as Partial<TuiSheetOptions>
            )
            .pipe(
                tap(({ orderType, table, isChanged }) => {
                    if (!isChanged) {
                        return;
                    }
                    const data: CartFillable = { order_type: orderType };

                    if (table) {
                        data.order_table = table;
                    }

                    this.store.dispatch(CartActions.updateCart(data));
                })
            )
            .subscribe();
    }
}
